import React, {useEffect, useState} from 'react';
import {Card, Grid, Typography, useTheme} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useLocation} from 'react-router-dom';
import {getPageData} from '@shared/services/service-handlers/PrivateService';
import FormConstants from '@shared/constants/FormConstants';
import {getErrorMessage} from '@shared/utils/ErrorUtils';

import DocumentsComponent from '@components/documents/DocumentsComponent';
import ProfilePage from '@components/profile/ProfilePage';
import CardComponent from '@components/card/CardComponent';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import VitalsComponent from '@components/vitals/VitalsComponent';
import TabsComponent from '../tabsComponent/TabsComponent';
import CollapsibleTable from '../collapsible-table/CollapsibleTable';
import AuthUtils from 'src/utils/AuthUtils';
import BenefitsComponent from '@components/benefits/BenefitsComponent';
import EncryptUtils from 'src/utils/EncryptUtils';
import ReportsComponent from '@components/reports/ReportsComponent';
import {useModuleContext} from '@context/ModuleContext';
import {useAppContext} from '@context/AppContext';
import {PulseLoader} from 'react-spinners';
import DicomComponent from '@components/dicom/DicomComponent';
import {generateSubModuleIdentifier} from 'src/utils/helperUtils';
import {GridEventListener, GridRowId} from '@mui/x-data-grid-premium';
import {AI_BLOB_SEARCH, LOCAL_SEARCH, TRIGGER} from 'src/routes/BaseRoute';

interface SubModuleSearchResultsProps {
  sectionUIData: any;
  module: any;
  sub_module: any;
  record_id: any;
  specimen_number_id?: any;
  specimen_number?: any;
  specimen_type?: any;
  accessionNumber?:any;
  procedure_id?: any;
  demographic_id?: any;
  handleCheckBoxClick?: any;
  filteredcheckData?: any;
  clearSelectedDataCount?: any;
  unique_id?: string;
  legacyApp?: any;
  ParentLegacyValue?: string;
  state?: any;
  setState?: React.Dispatch<any>;
  tableRefresh?: boolean;
  setTableRefresh?: (value: boolean) => void;
  documentsSearchTerm?: string;
}

const SubModulePageSection = React.memo(
  (props: SubModuleSearchResultsProps): React.ReactElement | any => {
    const {state, setState} = props;
    const theme = useTheme();
    const {
      appState: {applicationName}
    } = useAppContext();
    const {enqueueSnackbar} = useSnackbar();
    const [expandedRowIds, setExpandedRowIds] = React.useState<
      GridRowId[] | undefined
    >([]);
    const location = useLocation();
    const {state: locationState} = location;
    const data = locationState?.rowData;
    const [sectionValues, setSectionValues]: any = React.useState(null);
    const {
      sectionUIData,
      handleCheckBoxClick,
      filteredcheckData,
      clearSelectedDataCount,
      ParentLegacyValue
    } = props;
    const [loader, setLoader] = useState<boolean>(false);
    const {moduleState} = useModuleContext();
    const {isBH, isBHReason}: any = moduleState;

    const localSearch = EncryptUtils.getDecryptedData(
      location?.search,
      LOCAL_SEARCH
    );

    const aiBlobSearch = EncryptUtils.getDecryptedData(
      location?.search,
      AI_BLOB_SEARCH
    );

    const queryParams = new URLSearchParams(location.search);
    const trigger = queryParams.get(TRIGGER);

    const parsedAiBlobSearch = aiBlobSearch ? JSON.parse(aiBlobSearch) : {};

    const isAiBlobSearchActive =
      Object.keys(parsedAiBlobSearch)?.length > 0 &&
      parsedAiBlobSearch?.is_ai_blob_search;

    const {submodule} = state;

    useEffect(() => {
      if (
        props?.module === FormConstants.MODULE.RIS &&
        props?.sub_module.toLowerCase() ==
          FormConstants.SUB_MODULE.EXAM_PROCEDURE_LIST &&
        setState
      ) {
        const updatedState = {
          ...state,
          uiConfig: sectionUIData,
          sub_sections: sectionUIData?.sub_sections,
          ...(localSearch?.length ? JSON.parse(localSearch) : {})
        };
        setState(updatedState);
      }
    }, [props?.module, props?.sub_module, setState, sectionUIData]);

    const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();
    const init = async () => {
      try {
        if (
          props?.module &&
          props?.sub_module &&
          sectionUIData?.sql_data?.sql &&
          sectionUIData?.sql_data?.variables &&
          sectionUIData?.type != FormConstants.PAGE_SECTION_TYPES.DATA_GRID
        ) {
          const {specimen_number_id, procedure_id, demographic_id} = props;
          const {SpecimenNumber = null} = state;
          const procedure_type = state.data?.row?.ProcedureType;
          const data: any = {
            sql_data: sectionUIData?.sql_data,
            count_query_sql: sectionUIData?.count_query_sql,
            user_id: props?.record_id,
            record_id: props?.record_id,
            audit_identifier: AuthUtils.getAuditIdentifier(),
            audit_identifier_type: AuthUtils.getAuditIdentifierType(),
            audit_legacy_app: AuthUtils.getAuditLegacyApp(),
            audit_sub_module_identifier: generateSubModuleIdentifier(
              props?.module,
              submodule,
              {
                specimen_number_id: SpecimenNumber ?? specimen_number_id,
                procedure_id,
                procedure_type,
                demographic_id,
                ID: props?.record_id || '',
                sub_module_identifier_id:
                  sectionUIData?.sql_data?.sub_module_identifier_id
              }
            ),
            procedure_id:props?.procedure_id,
            specimen_number_id:props?.specimen_number_id,
            isBH_selected: sectionUIData?.sql_data?.ignoreIsBH
              ? 'ignore_isBH'
              : isBH,
            isBH_reason: isBHReason,
            application_name: applicationName
          };
          if (sectionUIData?.sql_data?.group_sql) {
            data.group_sql = sectionUIData?.sql_data?.group_sql;
          }

          if (
            sectionUIData?.is_filter_sql_required &&
            getLocaleStorageData.length > 0
          ) {
            data.filter_data = getLocaleStorageData;
          }

          setLoader(true);
          const encryptRequestData = EncryptUtils.encryptedData(data);
          const result = await getPageData(
            applicationName,
            props.module,
            props.sub_module,
            {
              tk: encryptRequestData
            }
          );
          setSectionValues(result?.data);
          setLoader(false);
        }
      } catch (e) {
        const errMessage = getErrorMessage(e);
        enqueueSnackbar(errMessage, SnackBarConfig.getError());
        setLoader(false);
      }
    };

    React.useEffect(() => {
      init();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleCellClick: GridEventListener<'cellClick'> = params => {
      // const colList = sectionUIData?.other_results;
    // console.log('function calling..sbps.');

      const row = params.row;
      if ((params as any)?.colDef?.field !== '__detail_panel_toggle__')
        return expandedRowIds;
      // Try to get a valid unique ID from the row
      const possibleIds = [
        row?.ID ||
          row?.OrderID ||
          row?.id ||
          row?.PatientID ||
          row?.first_name ||
          row?.BudgetID ||
          row?.Name ||
          row?.statId ||
          row?.ResultName ||
          row?.Priority ||
          row?.CodeSystem ||
          row?.Date ||
          row?.EventCode ||
          row?.CreatedDate ||
          row?.SubscriberNumber ||
          row?.Order ||
          row?.PurchaseOrderID ||
          row?.RequisitionID ||
          row?.InvoiceID ||
          row?.InvoiceNumber ||
          row?.Sequence ||
          row?.EventLevelID ||
          row?.VoucherID ||
          row?.PaymentID ||
          row?.AccountID ||
          row?.JournalID ||
          row?.Period1 ||
          row?.CorporationID ||
          row?.ItemID ||
          row?.ContractID ||
          row?.PayStatus ||
          row?.ItemVendorID ||
          row?.Year ||
          row?.EmpNo ||
          row?.DeductionName ||
          row?.ProviderName ||
          row?.LegacyMRN ||
          row?.LegacyApp
      ];

      // Pick the first non-undefined ID

      setExpandedRowIds(prev => {
        // Check if all possibleIds are already expanded
        if (prev !== undefined && prev.length === 0) {
          const allExpanded = possibleIds.every(id => prev.includes(id));

          if (allExpanded) {
            // If all IDs are already expanded, remove them
            return prev.filter(id => !possibleIds.includes(id));
          } else {
            // If not all are expanded, add missing IDs
            const newIds = possibleIds.filter(id => !prev.includes(id));
            return [...prev, ...newIds];
          }
        }
      });

      // for (const field of colList.fields) {
      //   if (params?.field === field.data_column && field?.is_clickable) {
      //     AuthUtils.setAuditIdentifier(params?.value);
      //     AuthUtils.setAuditIdentifierType(params?.colDef?.headerName);
      //     AuthUtils.setAuditLegacyApp(params?.row?.LegacyApp);

      //     const url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}/${BASE_ROUTE_SUB_MODULE}?${PARAMS_KEY}=${paramsKey}`;
      //     // if (props?.menuItem) {
      //     //   url += `&${BASE_MENU_ITEM}=${props.menuItem}`;
      //     // }
      //     // navigate(url);
      //   }
      // }
    };

    useEffect(() => {
      if (
        props?.module === FormConstants.MODULE.RIS &&
        props?.sub_module === FormConstants.SUB_MODULE.EXAM_PROCEDURE_LIST &&
        setState &&
        isAiBlobSearchActive
      ) {
        // First, reset submodule to false
        setState((prev: any) => ({
          ...prev,
          submodule: false
        }));

        // Then set submodule to true with updated uiConfig after a tick
        setTimeout(() => {
          const updatedState = {
            ...state,
            uiConfig: sectionUIData,
            sub_sections: sectionUIData?.sub_sections,
            ...(localSearch?.length ? JSON.parse(localSearch) : {}),
            submodule: true // Set again freshly
          };
          setState(updatedState);
        }, 0); // 0ms timeout to ensure a re-render
      }
    }, [trigger]); // depends on the trigger value in the URL

    const renderDataGridComponent = () => {
      const getSubSections = sectionUIData?.sub_sections?.find(
        (obj: any) => obj[state?.data?.row?.ProcedureType?.toLowerCase()]
      );

      return (
        <Grid>
          {submodule === true &&
          sectionUIData?.sub_section_type ===
            FormConstants.SUB_MODULE.SUB_SUB_MODULE &&
          getSubSections ? (
            <TabsComponent
              sectionUIData={
                getSubSections[state?.data?.row?.ProcedureType?.toLowerCase()]
              }
              sectionRowsData={[]}
              module={props?.module}
              sub_module={props?.sub_module}
              patient_id={props?.record_id}
              legacyApp={props?.legacyApp}
              unique_id={props?.unique_id}
              procedure_type={state.data?.row?.ProcedureType}
              accession_number={state?.data?.row?.AccessionNumber}
              specimen_number_id={props?.specimen_number_id}
              specimen_number={props?.specimen_number}
              demographic_id={props?.demographic_id}
              procedure_id={
                state?.data?.row?.ProcedureType
                  ? state?.data?.row?.ID
                  : props?.procedure_id
              }
            />
          ) : (
            <Card sx={{my: 2, boxShadow: 5, borderRadius: '8px'}}>
              <CollapsibleTable
                sectionUIData={sectionUIData}
                sectionRowsData={['submoduledatapassed']}
                module={props?.module}
                sub_module={props?.sub_module}
                recordId={props?.record_id}
                handleCheckBoxClick={handleCheckBoxClick}
                filteredcheckData={filteredcheckData}
                clearSelectedDataCount={clearSelectedDataCount}
                legacyApp={props?.legacyApp}
                ParentLegacyValue={ParentLegacyValue}
                setState={setState}
                setExpandedRowIds={setExpandedRowIds}
                expandedRowIds={expandedRowIds}
                onCellClick={handleCellClick}
                specimen_number_id={props?.specimen_number_id}
                specimen_number={props?.specimen_number}
                procedure_id={props?.procedure_id}
                procedure_type={state.data?.row?.ProcedureType}
                demographic_id={props?.demographic_id}
                tableRefresh={props?.tableRefresh}
                setTableRefresh={props?.setTableRefresh}
              />
            </Card>
          )}
        </Grid>
      );
    };

    const renderCardComponent = () => {
      return (
        <CardComponent
          sectionUIData={sectionUIData}
          recordId={props?.record_id}
          specimen_number_id={props?.specimen_number_id}
          specimen_number={props?.specimen_number}
          procedure_id={props?.procedure_id}
          procedure_type={state.data?.row?.ProcedureType}
          demographic_id={props?.demographic_id}
          fields={sectionUIData?.fields}
          sub_module={props?.sub_module}
          module={props?.module}
          data={sectionValues?.data?.[sectionUIData?.name] || sectionValues}
          columns_per_row={sectionUIData?.columns}
          is_border_required={sectionUIData?.is_border_required}
          is_space_needed_between_label_value={
            sectionUIData?.is_space_needed_between_label_value
          }
          is_colon_needed={sectionUIData?.is_colon_required}
        />
      );
    };

    const renderTabsComponent = () => {
      return (
        <TabsComponent
          sectionUIData={sectionUIData}
          sectionRowsData={sectionValues}
          specimen_number_id={props?.specimen_number_id}
          specimen_number={props?.specimen_number}
          specimen_type={data?.OrderType ?? props?.specimen_type}
          procedure_id={props?.procedure_id}
          procedure_type={state.data?.row?.ProcedureType}
          demographic_id={props?.demographic_id}
          module={props?.module}
          sub_module={props?.sub_module}
          patient_id={props?.record_id}
          legacyApp={props?.legacyApp}
          unique_id={props?.unique_id}
          ParentLegacyValue={ParentLegacyValue}
        />
      );
    };

    const renderDocumentComponent = () => {
      return (
        <DocumentsComponent
          subModuleUIData={sectionUIData}
          subModuleData={sectionValues}
          module={props?.module}
          sub_module={props?.sub_module}
          patient_id={props?.record_id}
          legacyApp={props?.legacyApp}
          unique_id={props?.unique_id}
          ParentLegacyValue={ParentLegacyValue}
          accession_number={props?.accessionNumber}
          specimen_number_id={props?.specimen_number_id}
          height={'80vh'}
          externalSearchTerm={props?.documentsSearchTerm}
        />
      );
    };

    const renderDicomComponent = () => {
      return (
        <DicomComponent
          subModuleUIData={sectionUIData}
          patient_id={props?.record_id}
          height={'80vh'}
        />
      );
    };

    const renderVitalComponent = () => {
      return (
        <VitalsComponent
          sectionUIData={sectionUIData}
          sectionRowsData={sectionValues}
          ParentLegacyValue={ParentLegacyValue}
        />
      );
    };

    const renderBenefitsComponent = () => {
      return (
        <BenefitsComponent
          sectionUIData={sectionUIData}
          sectionRowsData={sectionValues}
        />
      );
    };

    const renderReportsComponent = () => {
      return (
        <ReportsComponent
          sectionUIData={sectionUIData}
          sectionRowsData={sectionValues}
          module={props?.module}
          subModule={props?.sub_module}
        />
      );
    };

    const renderContent = () => {
      if (sectionUIData?.type === FormConstants.PAGE_SECTION_TYPES.DATA_GRID) {
        return renderDataGridComponent();
      } else if (
        sectionUIData?.type === FormConstants.PAGE_SECTION_TYPES.TABS
      ) {
        if (
          sectionUIData?.sub_module === FormConstants.REPORTS_SCREENS.REPORTS
        ) {
          return renderReportsComponent();
        } else {
          return renderTabsComponent();
        }
      } else if (
        sectionUIData?.type === FormConstants.PAGE_SECTION_TYPES.PROFILE
      ) {
        return (
          sectionValues && (
            <ProfilePage
              labels={props.sectionUIData}
              values={sectionValues?.[0]}
            />
          )
        );
      } else if (
        sectionUIData.type === FormConstants.PAGE_SECTION_TYPES.DOCUMENTS
      ) {
        return renderDocumentComponent();
      } else if (
        props?.module === FormConstants.MODULE.CLINICAL &&
        props?.sub_module === FormConstants.SUB_MODULE.DICOM &&
        sectionUIData.type === FormConstants.PAGE_SECTION_TYPES.DICOM
      ) {
        return renderDicomComponent();
      } else if (sectionUIData.type === FormConstants.PAGE_SECTION_TYPES.CARD) {
        return sectionValues && renderCardComponent();
      } else if (
        sectionUIData?.type === FormConstants.PAGE_SECTION_TYPES.SELECT
      ) {
        if (
          sectionUIData?.name === FormConstants.SCREENS.VITAL ||
          sectionUIData?.name === FormConstants.SCREENS.FLOWSHEETS
        ) {
          return renderVitalComponent();
        } else if (sectionUIData?.name === FormConstants.SCREENS.BENEFITS) {
          return renderBenefitsComponent();
        } else {
          return '';
        }
      } else {
        return <Typography>{`Page section Component:`}</Typography>;
      }
    };

    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          {loader &&
            !(
              sectionUIData?.type ===
                FormConstants.PAGE_SECTION_TYPES.PROFILE ||
              sectionUIData?.type === FormConstants.PAGE_SECTION_TYPES.SELECT ||
              sectionUIData.type ===
                FormConstants.PAGE_SECTION_TYPES.DOCUMENTS ||
              sectionUIData?.type === FormConstants.PAGE_SECTION_TYPES.TABS
            ) && (
              <Card
                sx={{
                  p: 3,
                  mt: 3,
                  boxShadow: 5,
                  borderRadius: '8px',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                <PulseLoader size={12} color={theme.palette.primary.main} />
              </Card>
            )}
        </div>
        {renderContent()}
      </>
    );
  }
);

export default SubModulePageSection;
